/**
 * Admin config block for the Property Article module.
 *
 * Lets the admin:
 *   - set a Theme (topic prompt) baked into every article the module generates
 *   - persist a heading + subheading rendered above the latest article
 *   - configure the schedule (enabled, frequency, day/hour)
 *   - hit "Generate Now" which fires POST /api/property-articles/generate
 *     with the current theme so a fresh article appears immediately
 *
 * The scheduler config is stored server-side keyed by module `instance_id`,
 * but the caller (`ModuleLibrary`) doesn't yet know its instance id — the
 * schedule persists on the instance side once the admin picks or edits an
 * instance. Here we treat it as UI-only state that ships alongside the
 * `configFields` so an integrator can wire it up.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ColorPickerInput } from "@/components/ui/ColorPickerInput";
import { pollAiJob } from "@/utils/pollAiJob";
import { Loader2, Sparkles, Clock, Trash2, RefreshCw, ExternalLink, ImageIcon, Check } from "lucide-react";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";

const API = getApiUrl();

const FREQUENCIES = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function PropertyArticleConfigFields({ config, onChange, instanceId }) {
  const [generating, setGenerating] = useState(false);
  const [articles, setArticles] = useState([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);
  useLockBodyScroll(!!preview);
  // Scheduler persistence — separate from the generic module-config
  // autosave. The hourly cron (`run_due_article_schedules` in
  // routes/property_articles.py) reads exclusively from the
  // `property_article_schedulers` collection via GET/PUT
  // /property-articles/scheduler/{instance_id}, NOT from this module's
  // generic `config` blob — so schedule fields must be explicitly synced.
  const [schedulerSaving, setSchedulerSaving] = useState(false);
  const [schedulerInfo, setSchedulerInfo] = useState(null);
  // Cover picker state for the currently-previewed article. Populated
  // lazily when `preview` opens by calling
  // `GET /property-articles/{id}/cover-options`. Persisted via
  // `PATCH /property-articles/{id}` on selection.
  const [coverOptions, setCoverOptions] = useState([]);
  const [coverLoading, setCoverLoading] = useState(false);
  const [coverSaving, setCoverSaving] = useState(false);
  const setField = (key, value) => onChange((prev) => ({ ...prev, [key]: value }));

  // Style keys the admin can override per-instance. Split by panel so each
  // "Reset" button clears only its own controls without disturbing sibling
  // panels or non-style config (heading text, schedule, etc.).
  const HEADING_STYLE_KEYS = [
    "heading_font_family",
    "heading_font_size",
    "heading_font_weight",
    "heading_align",
    "heading_color",
    "heading_transform",
    "subheading_font_size",
    "subheading_align",
  ];
  const TITLE_STYLE_KEYS = [
    "title_font_family",
    "title_font_size",
    "title_font_weight",
    "title_align",
    "title_color",
    "title_transform",
  ];

  const resetKeys = (keys) => {
    onChange((prev) => {
      const next = { ...(prev || {}) };
      keys.forEach((k) => delete next[k]);
      return next;
    });
  };

  const fetchArticles = async () => {
    setArticlesLoading(true);
    try {
      const { data } = await axios.get(`${API}/property-articles`, {
        params: { limit: 100 },
        withCredentials: true,
      });
      setArticles(data?.items || []);
    } catch {
      setArticles([]);
    } finally {
      setArticlesLoading(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  // Load the persisted scheduler record (separate store from module config)
  // so the panel can show accurate Next/Last run info instead of just the
  // locally-edited (possibly unsaved) fields.
  useEffect(() => {
    if (!instanceId) return;
    let alive = true;
    axios
      .get(`${API}/property-articles/scheduler/${instanceId}`)
      .then(({ data }) => { if (alive) setSchedulerInfo(data); })
      .catch(() => { if (alive) setSchedulerInfo(null); });
    return () => { alive = false; };
  }, [instanceId]);

  const handleSaveSchedule = async () => {
    if (!instanceId) {
      toast.error("Save this instance first, then reopen it to configure the schedule.");
      return;
    }
    setSchedulerSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/property-articles/scheduler/${instanceId}`,
        {
          instance_id: instanceId,
          enabled: !!config.schedule_enabled,
          theme: config.themes || config.theme || "",
          frequency: config.schedule_frequency || "weekly",
          hour: Number(config.schedule_hour ?? 9),
          day_of_week: Number(config.schedule_day_of_week ?? 1),
          day_of_month: Number(config.schedule_day_of_month ?? 1),
          word_count: Number(config.word_count) || 750,
        },
        { withCredentials: true }
      );
      setSchedulerInfo(data);
      toast.success(
        data.enabled
          ? `Schedule saved — next run ${new Date(data.next_run_at).toLocaleString("en-GB")}`
          : "Schedule saved (disabled)"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save schedule");
    } finally {
      setSchedulerSaving(false);
    }
  };

  const handleDelete = async (article_id) => {
    if (!confirm("Delete this article? This can't be undone.")) return;
    setBusyId(article_id);
    try {
      await axios.delete(`${API}/property-articles/${article_id}`, { withCredentials: true });
      setArticles((cur) => cur.filter((a) => a.article_id !== article_id));
      toast.success("Article deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleRegenerate = async (article_id) => {
    setBusyId(article_id);
    try {
      const { data } = await axios.post(
        `${API}/property-articles/${article_id}/regenerate`,
        {},
        { withCredentials: true }
      );
      const article = await pollAiJob(data.job_id);
      toast.success(`Regenerated: "${article.title}"`);
      setArticles((cur) => {
        const rest = cur.filter((a) => a.article_id !== article_id);
        return [article, ...rest];
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Regeneration failed");
    } finally {
      setBusyId(null);
    }
  };

  // "themes" is stored as a multi-line string ("one theme per line"). We
  // split on newlines OR commas when generating so the AI gets ONE theme
  // at random per article — keeps every run fresh. Comma-splitting also
  // covers legacy instances that saved a single comma-joined `theme`
  // string before the multi-line "themes" textarea existed.
  const pickRandomTheme = () => {
    const bag = String(config.themes || config.theme || "")
      .split(/[\n,]/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (bag.length === 0) return "";
    return bag[Math.floor(Math.random() * bag.length)];
  };

  const handleGenerateNow = async () => {
    const theme = pickRandomTheme();
    if (!theme) {
      toast.error("Add at least one theme (one per line) — the AI needs a topic.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/property-articles/generate`,
        {
          theme,
          word_count: Number(config.word_count) || 750,
        },
        { withCredentials: true }
      );
      const article = await pollAiJob(data.job_id);
      toast.success(`Article generated: "${article.title}"`);
      // Optimistically prepend to the CRUD list so the admin sees it appear
      setArticles((cur) => [article, ...cur]);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Failed to generate article");
    } finally {
      setGenerating(false);
    }
  };

  // Load cover-picker options whenever the preview modal opens for a
  // new article. The endpoint returns one option per referenced property
  // (only those with a hero image) plus the article's current cover.
  useEffect(() => {
    if (!preview?.article_id) {
      setCoverOptions([]);
      return;
    }
    let alive = true;
    setCoverLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/property-articles/${preview.article_id}/cover-options`,
          { withCredentials: true }
        );
        if (alive) setCoverOptions(data?.options || []);
      } catch {
        if (alive) setCoverOptions([]);
      } finally {
        if (alive) setCoverLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [preview?.article_id]);

  const selectCover = async (image) => {
    if (!preview) return;
    setCoverSaving(true);
    try {
      await axios.patch(
        `${API}/property-articles/${preview.article_id}`,
        { cover_image: image },
        { withCredentials: true }
      );
      // Keep local state in sync so a re-open reflects the pick.
      setPreview((cur) => (cur ? { ...cur, cover_image: image } : cur));
      setArticles((cur) =>
        cur.map((x) =>
          x.article_id === preview.article_id ? { ...x, cover_image: image } : x
        )
      );
      toast.success("Cover updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update cover");
    } finally {
      setCoverSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="property-article-config">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
        This module reads the 5 most recent live listings and uses AI to write a
        fresh article per the theme below. The <strong>latest article</strong>
        appears on the page in full; older ones appear as clickable summaries.
      </div>

      <div className="space-y-2">
        <Label>Heading (shown above the latest article)</Label>
        <Input
          value={config.heading || ""}
          placeholder="Property Insights"
          onChange={(e) => setField("heading", e.target.value)}
          data-testid="property-article-heading"
        />
      </div>

      <div className="space-y-2">
        <Label>Subheading</Label>
        <Input
          value={config.subheading || ""}
          placeholder="AI-curated market takes, grounded in the listings we're seeing right now."
          onChange={(e) => setField("subheading", e.target.value)}
          data-testid="property-article-subheading"
        />
      </div>

      <div className="space-y-2">
        <Label>Themes *</Label>
        <Textarea
          value={config.themes || config.theme || ""}
          placeholder={
            "One theme per line — the AI picks one at random per article.\n\n" +
            "e.g.\n" +
            "First-time buyer opportunities in Hertfordshire this spring\n" +
            "Family homes with garden space near good schools\n" +
            "How the latest Bank of England move affects local buyers"
          }
          onChange={(e) => setField("themes", e.target.value)}
          rows={6}
          data-testid="property-article-themes"
        />
        <p className="text-xs text-muted-foreground">
          Add multiple themes (one per line) — every generation picks one at
          random so each article stays fresh. The AI weaves the theme through
          the piece, referencing your latest listings plus current UK market
          context.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Word count (per article)</Label>
        <Input
          type="number"
          min={150}
          max={2500}
          step={50}
          value={config.word_count ?? 750}
          onChange={(e) => setField("word_count", Number(e.target.value))}
          data-testid="property-article-word-count"
        />
        <p className="text-xs text-muted-foreground">
          Target length in words. Clamped to 150–2500. The AI aims within ±10%.
        </p>
      </div>

      {/* Module heading style — controls the "NEWS" pill + subheading at
          the top of the module (visible above every article). */}
      <div className="border border-border rounded-lg p-3 space-y-3 bg-muted/20" data-testid="property-article-heading-style">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-medium">Module heading style</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => resetKeys(HEADING_STYLE_KEYS)}
            data-testid="property-article-heading-reset"
            title="Clear these overrides so the label falls back to Site Styles defaults"
          >
            <RefreshCw className="h-3 w-3 mr-1" /> Reset
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Styles the &quot;{config.heading || "Property Insights"}&quot; label + subheading shown at the top of every article. Leave blank to inherit from Site Styles.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Font family</Label>
            <Input
              value={config.heading_font_family || ""}
              placeholder="Inherit"
              onChange={(e) => setField("heading_font_family", e.target.value)}
              data-testid="property-article-heading-font"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Font size (px)</Label>
            <Input
              type="number"
              min={10}
              max={64}
              placeholder="Auto"
              value={config.heading_font_size || ""}
              onChange={(e) => setField("heading_font_size", e.target.value ? Number(e.target.value) : "")}
              data-testid="property-article-heading-size"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Weight</Label>
            <select
              value={config.heading_font_weight || ""}
              onChange={(e) => setField("heading_font_weight", e.target.value)}
              className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
              data-testid="property-article-heading-weight"
            >
              <option value="">Auto</option>
              <option value="300">300 · Light</option>
              <option value="400">400 · Regular</option>
              <option value="500">500 · Medium</option>
              <option value="600">600 · Semibold</option>
              <option value="700">700 · Bold</option>
              <option value="800">800 · Extra bold</option>
              <option value="900">900 · Black</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Alignment</Label>
            <select
              value={config.heading_align || ""}
              onChange={(e) => setField("heading_align", e.target.value)}
              className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
              data-testid="property-article-heading-align"
            >
              <option value="">Auto (centre)</option>
              <option value="left">Left</option>
              <option value="center">Centre</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Colour</Label>
            <ColorPickerInput
              value={config.heading_color || ""}
              onChange={(v) => setField("heading_color", v)}
              compact
              testId="property-article-heading-color"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Text case</Label>
            <select
              value={config.heading_transform || "uppercase"}
              onChange={(e) => setField("heading_transform", e.target.value)}
              className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
              data-testid="property-article-heading-case"
            >
              <option value="none">As written</option>
              <option value="uppercase">UPPERCASE</option>
              <option value="capitalize">Capitalise Each Word</option>
              <option value="lowercase">lowercase</option>
            </select>
          </div>
        </div>

        <div className="pt-3 mt-1 border-t border-border/60 space-y-2">
          <Label className="text-xs font-medium text-muted-foreground">
            Subheading style (&quot;{config.subheading || "AI-curated market takes…"}&quot;)
          </Label>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Font size (px)</Label>
              <Input
                type="number"
                min={10}
                max={32}
                placeholder="Auto"
                value={config.subheading_font_size || ""}
                onChange={(e) => setField("subheading_font_size", e.target.value ? Number(e.target.value) : "")}
                data-testid="property-article-subheading-size"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Alignment</Label>
              <select
                value={config.subheading_align || ""}
                onChange={(e) => setField("subheading_align", e.target.value)}
                className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
                data-testid="property-article-subheading-align"
              >
                <option value="">Auto (centre)</option>
                <option value="left">Left</option>
                <option value="center">Centre</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Article title styling — overrides Site Styles for the H1 only. */}
      <div className="border border-border rounded-lg p-3 space-y-3 bg-muted/20" data-testid="property-article-title-style">
        <div className="flex items-center justify-between gap-2">
          <Label className="text-sm font-medium">Article title style</Label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => resetKeys(TITLE_STYLE_KEYS)}
            data-testid="property-article-title-reset"
            title="Clear these overrides so the article title falls back to Site Styles defaults"
          >
            <RefreshCw className="h-3 w-3 mr-1" /> Reset
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Optional per-instance overrides. Leave blank to inherit from Site Styles.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Font family</Label>
            <Input
              value={config.title_font_family || ""}
              placeholder="Inherit"
              onChange={(e) => setField("title_font_family", e.target.value)}
              data-testid="property-article-title-font"
            />
            <p className="text-[10px] text-muted-foreground">e.g. Montserrat, Playfair Display, Georgia, serif</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Font size (px)</Label>
            <Input
              type="number"
              min={16}
              max={120}
              placeholder="Auto"
              value={config.title_font_size || ""}
              onChange={(e) => setField("title_font_size", e.target.value ? Number(e.target.value) : "")}
              data-testid="property-article-title-size"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Weight</Label>
            <select
              value={config.title_font_weight || ""}
              onChange={(e) => setField("title_font_weight", e.target.value)}
              className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
              data-testid="property-article-title-weight"
            >
              <option value="">Auto</option>
              <option value="300">300 · Light</option>
              <option value="400">400 · Regular</option>
              <option value="500">500 · Medium</option>
              <option value="600">600 · Semibold</option>
              <option value="700">700 · Bold</option>
              <option value="800">800 · Extra bold</option>
              <option value="900">900 · Black</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Alignment</Label>
            <select
              value={config.title_align || ""}
              onChange={(e) => setField("title_align", e.target.value)}
              className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
              data-testid="property-article-title-align"
            >
              <option value="">Auto</option>
              <option value="left">Left</option>
              <option value="center">Centre</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Colour</Label>
            <ColorPickerInput
              value={config.title_color || ""}
              onChange={(v) => setField("title_color", v)}
              compact
              testId="property-article-title-color"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Text case</Label>
            <select
              value={config.title_transform || "none"}
              onChange={(e) => setField("title_transform", e.target.value)}
              className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
              data-testid="property-article-title-case"
            >
              <option value="none">As written</option>
              <option value="uppercase">UPPERCASE</option>
              <option value="capitalize">Capitalise Each Word</option>
              <option value="lowercase">lowercase</option>
            </select>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-lg p-3 space-y-3 bg-muted/20">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" /> Auto-generate schedule
          </Label>
          <Switch
            checked={!!config.schedule_enabled}
            onCheckedChange={(v) => setField("schedule_enabled", v)}
            data-testid="property-article-schedule-enabled"
          />
        </div>
        {config.schedule_enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Frequency</Label>
              <select
                value={config.schedule_frequency || "weekly"}
                onChange={(e) => setField("schedule_frequency", e.target.value)}
                className="w-full h-9 px-2 border border-input rounded-md bg-background text-sm"
                data-testid="property-article-schedule-frequency"
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hour (UTC)</Label>
              <Input
                type="number"
                min={0}
                max={23}
                value={config.schedule_hour ?? 9}
                onChange={(e) => setField("schedule_hour", Number(e.target.value))}
                data-testid="property-article-schedule-hour"
              />
            </div>
            {(config.schedule_frequency || "weekly") === "weekly" && (
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Day of week</Label>
                <div className="flex gap-1">
                  {DAYS.map((d, i) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setField("schedule_day_of_week", i)}
                      className={`flex-1 h-8 text-xs rounded border ${
                        (config.schedule_day_of_week ?? 1) === i
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-input"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {config.schedule_frequency === "monthly" && (
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Day of month (1–28)</Label>
                <Input
                  type="number"
                  min={1}
                  max={28}
                  value={config.schedule_day_of_month ?? 1}
                  onChange={(e) => setField("schedule_day_of_month", Number(e.target.value))}
                />
              </div>
            )}
            <p className="col-span-2 text-[11px] text-muted-foreground">
              Runs automatically via the hourly scheduler — click{" "}
              <strong>Save Schedule</strong> below to persist these settings,
              or use <em>Generate Now</em> to trigger a fresh article on demand.
            </p>
            <div className="col-span-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleSaveSchedule}
                disabled={schedulerSaving || !instanceId}
                className="w-full"
                data-testid="property-article-schedule-save"
              >
                {schedulerSaving ? (
                  <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Saving…</>
                ) : (
                  "Save Schedule"
                )}
              </Button>
              {!instanceId && (
                <p className="text-[11px] text-amber-800 mt-1">
                  Save this module instance first, then reopen it to enable the schedule.
                </p>
              )}
              {schedulerInfo?.enabled && schedulerInfo?.next_run_at && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="property-article-schedule-next-run">
                  Next run: {new Date(schedulerInfo.next_run_at).toLocaleString("en-GB")}
                  {schedulerInfo.last_run_at && (
                    <> · Last run: {new Date(schedulerInfo.last_run_at).toLocaleString("en-GB")}</>
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <Button
          type="button"
          onClick={handleGenerateNow}
          disabled={generating || !(config.themes || config.theme || "").trim()}
          className="w-full"
          data-testid="property-article-generate-now"
        >
          {generating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating (may take ~20s)…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Now
            </>
          )}
        </Button>
      </div>

      {/* CRUD — every article generated by any Property Article instance */}
      <div className="border border-border rounded-lg p-3 space-y-2" data-testid="property-article-crud">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Generated articles</Label>
            <p className="text-xs text-muted-foreground">
              Preview, regenerate or delete any article this module has produced.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={fetchArticles}
            data-testid="property-article-refresh"
          >
            <RefreshCw className="h-3 w-3" />
          </Button>
        </div>
        {articlesLoading ? (
          <p className="text-xs text-muted-foreground py-3 text-center">
            <Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Loading…
          </p>
        ) : articles.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3 text-center">
            No articles yet — hit <em>Generate Now</em> to produce the first one.
          </p>
        ) : (
          <div className="max-h-72 overflow-y-auto border border-border rounded">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground sticky top-0">
                <tr>
                  <th className="text-left px-2 py-1.5">Title</th>
                  <th className="text-left px-2 py-1.5 w-28">Generated</th>
                  <th className="text-right px-2 py-1.5 w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.article_id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        className="text-left font-medium hover:underline block truncate max-w-[260px]"
                        onClick={() => setPreview(a)}
                        data-testid={`inst-article-title-${a.article_id}`}
                      >
                        {a.title}
                      </button>
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">
                      <Clock className="inline h-3 w-3 mr-1" />
                      {new Date(a.generated_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                    </td>
                    <td className="px-2 py-1.5 text-right space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() => handleRegenerate(a.article_id)}
                        disabled={busyId === a.article_id}
                        data-testid={`inst-article-regen-${a.article_id}`}
                      >
                        {busyId === a.article_id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() => setPreview(a)}
                        data-testid={`inst-article-preview-${a.article_id}`}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-destructive"
                        onClick={() => handleDelete(a.article_id)}
                        disabled={busyId === a.article_id}
                        data-testid={`inst-article-delete-${a.article_id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[130] bg-black/70 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}
        >
          <div className="relative mx-auto my-8 w-[92vw] max-w-3xl bg-background rounded-md shadow-2xl p-6">
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute top-3 right-3 h-8 w-8 rounded-full border hover:bg-muted"
              aria-label="Close"
            >
              ×
            </button>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                <Clock className="h-3 w-3" /> {new Date(preview.generated_at).toLocaleString("en-GB")}
              </p>
              <h2 className="text-2xl font-serif mb-1">{preview.title}</h2>
              <p className="text-xs text-muted-foreground">Theme: {preview.theme}</p>
            </div>

            {/* Cover image picker — one thumbnail per property referenced
                by this article. Click to make it the article's saved
                cover_image (used as the hero + card thumbnail). Hidden
                entirely when the article references no properties with
                images. */}
            {(coverLoading || coverOptions.length > 0) && (
              <div
                className="mb-6 border border-border rounded-md p-3 bg-muted/20"
                data-testid="article-cover-picker"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <ImageIcon className="h-3.5 w-3.5" /> Cover image
                  </div>
                  {coverSaving && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                  )}
                </div>
                {coverLoading ? (
                  <p className="text-xs text-muted-foreground">Loading options…</p>
                ) : (
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {coverOptions.map((opt) => {
                      const active = preview.cover_image === opt.image;
                      return (
                        <button
                          key={opt.property_id}
                          type="button"
                          onClick={() => selectCover(opt.image)}
                          disabled={coverSaving || active}
                          className={`relative shrink-0 w-28 rounded-md overflow-hidden border-2 transition-all ${
                            active ? "border-primary ring-2 ring-primary/30" : "border-transparent hover:border-primary/50"
                          }`}
                          title={opt.title}
                          data-testid={`article-cover-option-${opt.property_id}`}
                        >
                          <img
                            src={opt.image}
                            alt={opt.title}
                            className="w-full h-20 object-cover"
                          />
                          <div className="p-1.5 text-left bg-white">
                            <p className="text-[10px] font-medium truncate">{opt.title}</p>
                          </div>
                          {active && (
                            <span className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-0.5">
                              <Check className="h-3 w-3" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <article
              className="prose max-w-none prose-img:rounded-md prose-img:my-4"
              dangerouslySetInnerHTML={{ __html: preview.content_html }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
